import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted } from "vue";
  import { useRoute } from "vue-router";
  import { useToast } from "vue-toastification";
  import colorSchemeApi from "@/services/api/colorSchemeApi";
  import {
    EstateColorSchemeInterface,
    RoomImageItem,
    EstateProductMaterial,
  } from "@/types/ColorSchemeInterface";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AdminEstateInteriorColours',
  setup(__props, { expose: __expose }) {
  __expose();

  const route = useRoute();
  const toast = useToast();
  const estateId = route.params.estateId as string;

  // ── View state ────────────────────────────────────────────────────────────────
  const view = ref<"list" | "detail">("list");
  const loading = ref(false);
  const schemes = ref<EstateColorSchemeInterface[]>([]);
  const selectedScheme = ref<EstateColorSchemeInterface | null>(null);
  const localRoomImages = ref<RoomImageItem[]>([]);
  const localMaterials = ref<EstateProductMaterial[]>([]);

  // ── Scheme modal ──────────────────────────────────────────────────────────────
  const showSchemeModal = ref(false);
  const isEditSchemeMode = ref(false);
  const savingScheme = ref(false);
  const schemeForm = ref({ name: "", description: "" });
  const editingSchemeId = ref<string | null>(null);

  // ── Room image modal ──────────────────────────────────────────────────────────
  const showRoomImageModal = ref(false);
  const isEditRoomImageMode = ref(false);
  const savingRoomImage = ref(false);
  const editingRoomImageIdx = ref<number | null>(null);
  const roomImageNameError = ref(false);
  const roomImageForm = ref({
    name: "",
    file: null as File | null,
    previewUrl: "",
    url: "",
  });
  const roomImageFileInput = ref<HTMLInputElement | null>(null);

  // ── Material modal ─────────────────────────────────────────────────────────────
  const showMaterialModal = ref(false);
  const isEditMaterialMode = ref(false);
  const savingMaterial = ref(false);
  const editingMaterialIdx = ref<number | null>(null);
  const materialNameError = ref(false);
  const materialForm = ref({
    name: "",
    description: "",
    file: null as File | null,
    previewUrl: "",
    url: "",
  });
  const materialFileInput = ref<HTMLInputElement | null>(null);

  // ── Data fetching ─────────────────────────────────────────────────────────────
  const fetchSchemes = async () => {
    try {
      loading.value = true;
      schemes.value = await colorSchemeApi.getColorSchemesByEstateId(estateId);
    } catch {
      toast.error("Failed to load colour schemes");
    } finally {
      loading.value = false;
    }
  };

  onMounted(fetchSchemes);

  // ── Scheme operations ─────────────────────────────────────────────────────────
  const openCreateModal = () => {
    isEditSchemeMode.value = false;
    schemeForm.value = { name: "", description: "" };
    editingSchemeId.value = null;
    showSchemeModal.value = true;
  };

  const openEditModal = (scheme: EstateColorSchemeInterface) => {
    isEditSchemeMode.value = true;
    schemeForm.value = {
      name: scheme.name,
      description: scheme.description || "",
    };
    editingSchemeId.value = scheme._id;
    showSchemeModal.value = true;
  };

  const closeSchemeModal = () => {
    showSchemeModal.value = false;
  };

  const saveScheme = async () => {
    if (!schemeForm.value.name.trim()) return;

    savingScheme.value = true;

    try {
      if (isEditSchemeMode.value && editingSchemeId.value) {
        await colorSchemeApi.updateEstateColorScheme(
          {
            name: schemeForm.value.name,
            description: schemeForm.value.description,
          },
          editingSchemeId.value
        );
        toast.success("Colour scheme updated");
      } else {
        await colorSchemeApi.createEstateColorScheme({
          name: schemeForm.value.name,
          description: schemeForm.value.description,
          estateId,
          roomImagesList: [],
          productMaterials: [],
        });
        toast.success("Colour scheme created");
      }

      closeSchemeModal();
      await fetchSchemes();
    } catch {
      toast.error("Failed to save colour scheme");
    } finally {
      savingScheme.value = false;
    }
  };

  const confirmDeleteScheme = async (scheme: EstateColorSchemeInterface) => {
    if (!confirm(`Delete colour scheme "${scheme.name}"?`)) return;

    try {
      await colorSchemeApi.deleteEstateColorScheme(scheme._id);
      toast.success("Colour scheme deleted");
      await fetchSchemes();
    } catch {
      toast.error("Failed to delete colour scheme");
    }
  };

  // ── Detail view ───────────────────────────────────────────────────────────────
  const openDetail = (scheme: EstateColorSchemeInterface) => {
    selectedScheme.value = scheme;
    localRoomImages.value = scheme.roomImagesList
      ? [...scheme.roomImagesList]
      : [];
    localMaterials.value = scheme.productMaterials
      ? [...scheme.productMaterials]
      : [];
    view.value = "detail";
  };

  const patchScheme = async (data: {
    roomImagesList?: RoomImageItem[];
    productMaterials?: EstateProductMaterial[];
  }) => {
    if (!selectedScheme.value) return;

    await colorSchemeApi.updateEstateColorScheme(
      data,
      selectedScheme.value._id
    );
    const idx = schemes.value.findIndex(
      (s) => s._id === selectedScheme.value!._id
    );

    if (idx !== -1) {
      if (data.roomImagesList !== undefined)
        schemes.value[idx].roomImagesList = data.roomImagesList;

      if (data.productMaterials !== undefined)
        schemes.value[idx].productMaterials = data.productMaterials;
    }
  };

  // ── Room image operations ─────────────────────────────────────────────────────
  const openAddRoomImageModal = () => {
    isEditRoomImageMode.value = false;
    editingRoomImageIdx.value = null;
    roomImageForm.value = { name: "", file: null, previewUrl: "", url: "" };
    roomImageNameError.value = false;
    showRoomImageModal.value = true;
  };

  const openEditRoomImageModal = (img: RoomImageItem, idx: number) => {
    isEditRoomImageMode.value = true;
    editingRoomImageIdx.value = idx;
    roomImageForm.value = {
      name: img.name,
      file: null,
      previewUrl: "",
      url: img.url || "",
    };
    roomImageNameError.value = false;
    showRoomImageModal.value = true;
  };

  const closeRoomImageModal = () => {
    if (roomImageForm.value.previewUrl)
      URL.revokeObjectURL(roomImageForm.value.previewUrl);

    showRoomImageModal.value = false;
  };

  const handleRoomImageFileChange = (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0];

    if (!file) return;

    if (roomImageForm.value.previewUrl)
      URL.revokeObjectURL(roomImageForm.value.previewUrl);

    roomImageForm.value.file = file;
    roomImageForm.value.previewUrl = URL.createObjectURL(file);
  };

  const saveRoomImage = async () => {
    if (!roomImageForm.value.name.trim()) {
      roomImageNameError.value = true;
      return;
    }

    if (!selectedScheme.value) return;

    savingRoomImage.value = true;

    try {
      let finalUrl = roomImageForm.value.url;

      if (roomImageForm.value.file) {
        const formData = new FormData();

        formData.append("file", roomImageForm.value.file);
        const result = await colorSchemeApi.uploadEstateImage(
          estateId,
          formData
        );

        finalUrl = result.url;
      }

      const newImages = [...localRoomImages.value];
      const newItem: RoomImageItem = {
        name: roomImageForm.value.name,
        url: finalUrl || undefined,
      };

      if (isEditRoomImageMode.value && editingRoomImageIdx.value !== null) {
        newImages[editingRoomImageIdx.value] = newItem;
      } else {
        newImages.push(newItem);
      }

      await patchScheme({ roomImagesList: newImages });
      localRoomImages.value = newImages;

      if (selectedScheme.value) selectedScheme.value.roomImagesList = newImages;

      toast.success(
        isEditRoomImageMode.value ? "Image updated" : "Image added"
      );
      closeRoomImageModal();
    } catch {
      toast.error("Failed to save image");
    } finally {
      savingRoomImage.value = false;
    }
  };

  const confirmDeleteRoomImage = async (idx: number) => {
    if (!confirm("Delete this room image?")) return;

    if (!selectedScheme.value) return;

    const newImages = localRoomImages.value.filter((_, i) => i !== idx);

    try {
      await patchScheme({ roomImagesList: newImages });
      localRoomImages.value = newImages;
      selectedScheme.value.roomImagesList = newImages;
      toast.success("Image deleted");
    } catch {
      toast.error("Failed to delete image");
    }
  };

  // ── Material operations ───────────────────────────────────────────────────────
  const openAddMaterialModal = () => {
    isEditMaterialMode.value = false;
    editingMaterialIdx.value = null;
    materialForm.value = {
      name: "",
      description: "",
      file: null,
      previewUrl: "",
      url: "",
    };
    materialNameError.value = false;
    showMaterialModal.value = true;
  };

  const openEditMaterialModal = (mat: EstateProductMaterial, idx: number) => {
    isEditMaterialMode.value = true;
    editingMaterialIdx.value = idx;
    materialForm.value = {
      name: mat.name,
      description: mat.description || "",
      file: null,
      previewUrl: "",
      url: mat.url || "",
    };
    materialNameError.value = false;
    showMaterialModal.value = true;
  };

  const closeMaterialModal = () => {
    if (materialForm.value.previewUrl)
      URL.revokeObjectURL(materialForm.value.previewUrl);

    showMaterialModal.value = false;
  };

  const handleMaterialFileChange = (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0];

    if (!file) return;

    if (materialForm.value.previewUrl)
      URL.revokeObjectURL(materialForm.value.previewUrl);

    materialForm.value.file = file;
    materialForm.value.previewUrl = URL.createObjectURL(file);
  };

  const saveMaterial = async () => {
    if (!materialForm.value.name.trim()) {
      materialNameError.value = true;
      return;
    }

    if (!selectedScheme.value) return;

    savingMaterial.value = true;

    try {
      let finalUrl = materialForm.value.url;

      if (materialForm.value.file) {
        const formData = new FormData();

        formData.append("file", materialForm.value.file);
        const result = await colorSchemeApi.uploadEstateImage(
          estateId,
          formData
        );

        finalUrl = result.url;
      }

      const newMaterials = [...localMaterials.value];
      const newItem: EstateProductMaterial = {
        name: materialForm.value.name,
        description: materialForm.value.description || undefined,
        url: finalUrl || undefined,
      };

      if (isEditMaterialMode.value && editingMaterialIdx.value !== null) {
        newMaterials[editingMaterialIdx.value] = newItem;
      } else {
        newMaterials.push(newItem);
      }

      await patchScheme({ productMaterials: newMaterials });
      localMaterials.value = newMaterials;

      if (selectedScheme.value)
        selectedScheme.value.productMaterials = newMaterials;

      toast.success(
        isEditMaterialMode.value ? "Material updated" : "Material added"
      );
      closeMaterialModal();
    } catch {
      toast.error("Failed to save material");
    } finally {
      savingMaterial.value = false;
    }
  };

  const confirmDeleteMaterial = async (idx: number) => {
    if (!confirm("Delete this material?")) return;

    if (!selectedScheme.value) return;

    const newMaterials = localMaterials.value.filter((_, i) => i !== idx);

    try {
      await patchScheme({ productMaterials: newMaterials });
      localMaterials.value = newMaterials;
      selectedScheme.value.productMaterials = newMaterials;
      toast.success("Material deleted");
    } catch {
      toast.error("Failed to delete material");
    }
  };

const __returned__ = { route, toast, estateId, view, loading, schemes, selectedScheme, localRoomImages, localMaterials, showSchemeModal, isEditSchemeMode, savingScheme, schemeForm, editingSchemeId, showRoomImageModal, isEditRoomImageMode, savingRoomImage, editingRoomImageIdx, roomImageNameError, roomImageForm, roomImageFileInput, showMaterialModal, isEditMaterialMode, savingMaterial, editingMaterialIdx, materialNameError, materialForm, materialFileInput, fetchSchemes, openCreateModal, openEditModal, closeSchemeModal, saveScheme, confirmDeleteScheme, openDetail, patchScheme, openAddRoomImageModal, openEditRoomImageModal, closeRoomImageModal, handleRoomImageFileChange, saveRoomImage, confirmDeleteRoomImage, openAddMaterialModal, openEditMaterialModal, closeMaterialModal, handleMaterialFileChange, saveMaterial, confirmDeleteMaterial, ref, onMounted, get useRoute() { return useRoute }, get useToast() { return useToast }, get colorSchemeApi() { return colorSchemeApi }, get EstateColorSchemeInterface() { return EstateColorSchemeInterface }, get RoomImageItem() { return RoomImageItem }, get EstateProductMaterial() { return EstateProductMaterial } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})